import React, { useEffect, useMemo, useState } from "react";
import SuggestionCard from "./SuggestionCard";
import { useDispatch, useSelector } from "react-redux";
import { getArtistStats, getAudienceDetails } from "../../../../redux/actions";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getTopSuggestions } from "../../../../helper/suggestions";
import { fetchLogExpenses, fetchLogRevenues, getStreamsData } from "../../../../redux/actions/artistManager";
import { getUserPurchases } from "../../../../redux/actions/billing";

function SuggestionsPanel() {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [dataFetched, setDataFetched] = useState(false);

  // Selectors
  const { artistStats } = useSelector(state => state.artistDetails);
  const artistDetailsBySource = useSelector((state) => state.dataanalyst);
  const artistManagerReducer = useSelector((state) => state.artistManager);
  const { hasActiveSubscription, purchaseByMonthForROI } = useSelector((state) => state.billing);

  const {
    artistStatsBySource,
    audienceDetails,
  } = artistDetailsBySource;

  // Memoized calculations to prevent unnecessary recalculations
  const financeData = useMemo(() => {
    const totalSubscriptionAmount = hasActiveSubscription
      ? (purchaseByMonthForROI?.statistics?.totalAmount || 0)
      : 0;
    const totalRevenues = artistManagerReducer?.loggedRevenuesForROI?.data?.totalAmount || 0;
    const totalExpenses = (artistManagerReducer?.loggedExpensesForROI?.data?.totalAmount || 0) + totalSubscriptionAmount;

    const spotifyStreamsData = artistManagerReducer?.spotifyDataForROI || {};
    const youtubeViewsData = artistManagerReducer?.youtubeDataForROI || {};
    const streamsPlusViews = (spotifyStreamsData?.streamsDelta || 0) + (youtubeViewsData?.streamsDelta || 0);
    const totalPublishingEstimate = (streamsPlusViews * 0.0004) + (streamsPlusViews * 0.00091);
    const totalStreamingEstimate =
      ((spotifyStreamsData?.streamsDelta || 0) * 0.0038) +
      ((youtubeViewsData?.streamsDelta || 0) * 0.0006);
    const totalRevenuesWithEstimates = totalRevenues + totalStreamingEstimate + totalPublishingEstimate;
    const roi = totalExpenses === 0 ? 0 : ((totalRevenuesWithEstimates - totalExpenses) / totalExpenses) * 100;

    return {
      revenues: artistManagerReducer?.loggedRevenuesForROI?.data?.revenues || [],
      expenses: artistManagerReducer?.loggedExpensesForROI?.data?.expenses || [],
      totalRevenues: totalRevenuesWithEstimates?.toFixed(2),
      totalExpenses: totalExpenses?.toFixed(2),
      highestCategory: "Marketing",
      roi: roi
    };
  }, [
    hasActiveSubscription,
    purchaseByMonthForROI?.statistics?.totalAmount,
    artistManagerReducer?.loggedRevenuesForROI?.data,
    artistManagerReducer?.loggedExpensesForROI?.data,
    artistManagerReducer?.spotifyDataForROI,
    artistManagerReducer?.youtubeDataForROI
  ]);

  const topTrack = useMemo(() => ({
    trackName: artistStatsBySource?.topTrack?.track_name,
    streams: artistStatsBySource?.topTrack?.streamsCount,
  }), [artistStatsBySource?.topTrack]);

  // Memoized suggestions processing
  const finalSuggestions = useMemo(() => {
    // Helper to normalize helper output into an array
    const ensureArray = (v) => {
      if (!v && v !== 0) return [];
      if (Array.isArray(v)) return v;
      return [v];
    };

    const suggestionFromArtistSource = ensureArray(getTopSuggestions(artistStats, '/admin/data-analyst'));
    const suggestionFromArtistAudience = ensureArray(getTopSuggestions(audienceDetails, '/admin/data-analyst/catalog'));
    const suggestionFromFinanceData = ensureArray(getTopSuggestions(financeData, '/admin/artist-manager/release-and-financial-planner'));
    const suggestionFromTopTrack = ensureArray(getTopSuggestions(topTrack, '/admin/data-analyst/catalog'));

    const combinedSources = [
      ...suggestionFromArtistSource,
      ...suggestionFromArtistAudience,
      ...suggestionFromFinanceData,
      ...suggestionFromTopTrack
    ];

    const getKey = (item) => {
      if (!item || typeof item !== 'object') return JSON.stringify(item);
      return item.id ?? item._id ?? item.name ?? item.title ?? item.key ?? JSON.stringify(item);
    };

    // Extract nested suggestions
    const nested = [];
    for (const src of combinedSources) {
      if (src && Array.isArray(src.suggestions) && src.suggestions.length > 0) {
        nested.push(...src.suggestions);
      } else {
        nested.push(src);
      }
    }

    // Deduplicate
    const seen = new Set();
    const deduped = [];
    for (const item of nested) {
      const k = getKey(item);
      if (!seen.has(k)) {
        seen.add(k);
        deduped.push(item);
      }
    }

    for (let i = deduped.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [deduped[i], deduped[j]] = [deduped[j], deduped[i]];
    }

    // Pick random 6 or 7 items
    const desiredCount = Math.min(deduped.length, Math.random() < 0.5 ? 6 : 7);
    return deduped.slice(0, desiredCount);
  }, [artistStats, audienceDetails, financeData, topTrack]);

  // Data fetching effect - runs only once on mount
  useEffect(() => {
    if (dataFetched) return; // Prevent duplicate API calls

    const fetchData = async () => {
      setIsLoading(true);
      const month = new Date().toLocaleString("en-US", { month: "short" });
      const year = new Date().getFullYear();
      const queryParams = { type: 'weekly' };

      try {
        // Dispatch all actions
        await Promise.all([
          dispatch(getArtistStats(toast, queryParams, () => { })),
          dispatch(getAudienceDetails(toast, { source: "all" }, () => { })),
          dispatch(fetchLogRevenues(toast, month, year, () => { }, "roi")),
          dispatch(fetchLogExpenses(toast, month, year, () => { }, "roi")),
          dispatch(getStreamsData(toast, { source: "spotify", month: month, year: year }, () => { }, "roi")),
          dispatch(getStreamsData(toast, { source: "youtube", month: month, year: year }, () => { }, "roi")),
        ]);
      } catch (error) {
        console.error("Error fetching suggestions data:", error);
      } finally {
        setIsLoading(false);
        setDataFetched(true);
      }
    };

    fetchData();
  }, [dispatch, dataFetched]);

  useEffect(() => {
    if (!hasActiveSubscription) return;

    const month = new Date().toLocaleString("en-US", { month: "short" });
    const year = new Date().getFullYear();
    dispatch(getUserPurchases({ month, year }, "roi"));
  }, [dispatch, hasActiveSubscription]);

  // Loading state
  if (isLoading) {
    return (
      <div className="lg:max-w-1/3 rounded-xl">
        <div>
          <h3 className="bg-gradient-to-r from-theme_clr1 to-theme_clr2 text-white rounded-lg font-bold mb-2 text-lg text-center p-2">
            Amplitude Suggestions
          </h3>
        </div>
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme_clr1"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">

      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-gradient-to-r from-theme_clr1 to-theme_clr2 text-white rounded-xl font-bold text-lg text-center p-2">
        Amplitude Suggestions
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 flex flex-col gap-3 min-h-0 overflow-y-auto suggestions-scroll pt-4">
        {finalSuggestions.length > 0 ? (
          finalSuggestions.map((s, idx) => (
            <SuggestionCard
              key={idx}
              suggestion={s.insight}
              route={s.route}
            />
          ))
        ) : (
          <div className="text-center p-4 text-gray-500">
            No suggestions available at the moment.
          </div>
        )}
      </div>

    </div>
  );
}

export default React.memo(SuggestionsPanel);